import { UAU_CONFIG } from '../config/constants';
import { ApiClient } from './api';

export enum SessionState {
  VALID = 'VALID',
  INVALID = 'INVALID',
  EXPIRED = 'EXPIRED',
  CHECKING = 'CHECKING',
  NETWORK_ERROR = 'NETWORK_ERROR'
}

// Interfaces de entrada
export interface LoginRequest {
  username: string;
  password: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

// Interfaces de empresa
export interface Empresa {
  id: number;
  nombre: string;
  rut?: string;
  es_activa?: boolean;
}

// Interface de usuario
export interface User {
  id: number;
  username: string;
  email: string;
  nombre_completo: string;
  es_activo: boolean;
  tipo_usuario: 'operador' | 'supervisor' | 'administrador';
  auth_service_id: string;
  permisos: any[];
  permisos_menu?: string[];
  empresas_asignadas: Empresa[];
  empresa_activa?: Empresa | null;
  created_at: string;
  updated_at: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: User;
}

export class AuthServiceUAU {
  private static readonly TOKEN_KEY = UAU_CONFIG.STORAGE_KEYS.TOKEN;
  private static readonly REFRESH_TOKEN_KEY = UAU_CONFIG.STORAGE_KEYS.REFRESH_TOKEN;
  private static readonly USER_KEY = UAU_CONFIG.STORAGE_KEYS.USER;
  private static readonly PERMISSIONS_KEY = UAU_CONFIG.STORAGE_KEYS.PERMISSIONS;

  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      // PASO 1: Login - obtener access_token
      // ⚠️ IMPORTANTE: Backend espera form-data, NO JSON
      const formData = new FormData();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);

      const loginData: any = await ApiClient.post('/api/auth/login', formData);

      const access_token = loginData.data?.access_token || loginData.access_token;

      if (!access_token) {
        console.error('Login data received:', loginData);
        throw new Error('No se recibió access_token del login');
      }

      // PASO 2: Obtener información del usuario (con token)
      const userInfoData: any = await ApiClient.get('/api/auth/user-info', {
        headers: {
          'Authorization': `Bearer ${access_token}`
        }
      });

      // PASO 3: Obtener permisos (con token)
      const permissionsData: any = await ApiClient.get('/api/auth/check-permissions', {
        headers: {
          'Authorization': `Bearer ${access_token}`
        }
      });

      // PASO 4: Mapear datos del backend a estructura frontend
      const mappedUser = this.mapBackendToFrontendUser(userInfoData, permissionsData, loginData);

      // PASO 5: Guardar datos en localStorage
      this.setTokenPrivate(access_token);
      this.setUser(mappedUser);
      this.setPermissions(permissionsData.permissions || {});

      // Configurar empresa activa si hay organizaciones
      if (userInfoData?.organizations && userInfoData.organizations.length > 0) {
        const firstOrg = userInfoData.organizations[0];
        this.setEmpresaActiva({
          id: firstOrg.id,
          nombre: firstOrg.name,
          rut: firstOrg.rut || '',
          es_activa: true
        });

        mappedUser.empresa_activa = {
          id: firstOrg.id,
          nombre: firstOrg.name,
          rut: firstOrg.rut || '',
          es_activa: true
        };
      }

      const response: LoginResponse = {
        access_token: access_token,
        refresh_token: '',
        user: mappedUser
      };

      return response;
    } catch (error: any) {
      // ⭐ WRAPPER YA HA MARCADO isSilentError
      // Solo relanzar el error tal como viene del ApiClient
      // El error ya contiene: isSilentError, message, original_status, error_type
      throw error;
    }
  }

  private static mapBackendToFrontendUser(
    userInfoData: any,
    permissionsData: any,
    loginData: any
  ): User {
    const backendPermissions = permissionsData.permissions || {};
    const organizations = userInfoData.organizations || loginData.organizations || [];

    const empresasAsignadas = organizations.map((org: any) => ({
      id: org.id,
      nombre: org.name,
      rut: org.rut || '',
      es_activa: true
    }));

    const permissionsArray = Object.keys(backendPermissions).filter(key => backendPermissions[key]);

    return {
      id: userInfoData.user_id,
      username: userInfoData.username,
      email: userInfoData.email || '',
      nombre_completo: userInfoData.full_name || userInfoData.username,
      es_activo: true,
      tipo_usuario: this.determineUserType(backendPermissions),
      auth_service_id: userInfoData.user_id.toString(),
      permisos: this.convertBackendPermissionsToRendiciones(backendPermissions),
      permisos_menu: permissionsArray,
      empresas_asignadas: empresasAsignadas,
      empresa_activa: empresasAsignadas[0] || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  private static convertBackendPermissionsToRendiciones(backendPermissions: Record<string, boolean>): any[] {
    const rendicionesPermissions: any[] = [];

    Object.entries(backendPermissions).forEach(([permission, hasPermission]) => {
      if (hasPermission) {
        rendicionesPermissions.push({
          id: Math.random(),
          codigo: permission,
          nombre: permission,
          descripcion: `Permiso ${permission}`,
          categoria: permission.split('.')[0] || 'general'
        });
      }
    });

    return rendicionesPermissions;
  }

  private static determineUserType(backendPermissions: Record<string, boolean>): 'operador' | 'supervisor' | 'administrador' {
    if (backendPermissions['admin.administrar']) {
      return 'administrador';
    }
    return 'operador';
  }

  static async logout(): Promise<void> {
    // JWT es stateless - no requiere notificar al servidor
    // El logout se completa limpiando el storage local
    this.clearStorage();
    // Pequeño delay para permitir que React complete la navegación
    // antes de que los componentes intenten hacer requests autenticados
    await new Promise(resolve => setTimeout(resolve, 50));
  }

  static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private static setTokenPrivate(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  private static setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  static getUser(): User | null {
    try {
      const userData = localStorage.getItem(this.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      return null;
    }
  }

  private static setPermissions(permissions: Record<string, boolean>): void {
    localStorage.setItem(this.PERMISSIONS_KEY, JSON.stringify(permissions));
  }

  static getUAUPermissions(): Record<string, boolean> {
    try {
      const permissionsData = localStorage.getItem(this.PERMISSIONS_KEY);
      return permissionsData ? JSON.parse(permissionsData) : {};
    } catch (error) {
      return {};
    }
  }

  static setEmpresaActiva(empresa: any): void {
    localStorage.setItem(UAU_CONFIG.STORAGE_KEYS.EMPRESA_ACTIVA, JSON.stringify(empresa));
  }

  static getEmpresaActiva(): any | null {
    try {
      const empresaData = localStorage.getItem(UAU_CONFIG.STORAGE_KEYS.EMPRESA_ACTIVA);
      return empresaData ? JSON.parse(empresaData) : null;
    } catch (error) {
      return null;
    }
  }

  private static clearStorage(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.PERMISSIONS_KEY);
    localStorage.removeItem(UAU_CONFIG.STORAGE_KEYS.EMPRESA_ACTIVA);
  }

  static isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  static isAdmin(): boolean {
    const user = this.getUser();
    return user?.tipo_usuario === 'administrador' || false;
  }

  static getCurrentUserInfo(): {
    user: User | null;
    isAuthenticated: boolean;
    isAdmin: boolean;
    isSuperuser: boolean;
    permissions: string[];
  } {
    const user = this.getUser();
    const isAuthenticated = this.isAuthenticated();
    const isAdmin = this.isAdmin();
    const isSuperuser = isAdmin; // Por ahora igual a admin
    const backendPermissions = this.getUAUPermissions();
    const permissions = Object.keys(backendPermissions).filter(key => backendPermissions[key]);

    return { user, isAuthenticated, isAdmin, isSuperuser, permissions };
  }

  // ===============================
  // MÉTODOS ADICIONALES (COMO EN p_rendicion)
  // ===============================

  /**
   * Obtiene el perfil actualizado del usuario
   */
  static async getProfile(): Promise<User> {
    const token = this.getToken();
    if (!token) throw new Error('No hay token de acceso');

    const userInfo: any = await ApiClient.get('/api/auth/user-info', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    await this.refreshPermissions();
    const backendPermissions = this.getUAUPermissions();
    const permissionsArray = Object.keys(backendPermissions).filter(key => backendPermissions[key]);

    const mappedUser: User = {
      id: userInfo.user_id || 0,
      username: userInfo.username,
      email: userInfo.email || '',
      nombre_completo: userInfo.full_name || userInfo.username,
      es_activo: userInfo.is_active || true,
      tipo_usuario: this.determineUserType(backendPermissions),
      auth_service_id: (userInfo.user_id || 0).toString(),
      permisos: this.convertBackendPermissionsToRendiciones(backendPermissions),
      permisos_menu: permissionsArray,
      empresas_asignadas: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    this.setUser(mappedUser);
    return mappedUser;
  }

  /**
   * Refresca los permisos del usuario actual
   */
  private static async refreshPermissions(): Promise<void> {
    try {
      const token = this.getToken();
      if (!token) return;

      const permissionsData: any = await ApiClient.get('/api/auth/check-permissions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      this.setPermissions(permissionsData.permissions || {});
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Refresca los permisos desde el contexto externo
   */
  static async refrescarPermisos(): Promise<void> {
    await this.refreshPermissions();
  }


  /**
   * Cambia la empresa activa del usuario
   */
  static async cambiarEmpresa(empresaId: number): Promise<void> {
    const user = this.getUser();
    if (!user || !user.empresas_asignadas) {
      throw new Error('Usuario no válido o sin empresas asignadas');
    }

    const empresa = user.empresas_asignadas.find(e => e.id === empresaId);
    if (!empresa) {
      throw new Error('Empresa no encontrada en las asignadas al usuario');
    }

    const token = this.getToken();
    if (!token) {
      throw new Error('No hay token de acceso');
    }

    try {
      await ApiClient.post(`/api/uau/switch-organization/${empresaId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      this.setEmpresaActiva({
        id: empresa.id,
        nombre: empresa.nombre,
        rut: empresa.rut,
        es_activa: true
      });

      const updatedUser = { ...user, empresa_activa: empresa };
      this.setUser(updatedUser);
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Verifica el estado actual de la sesión
   */
  static async checkSessionState(): Promise<SessionState> {
    try {
      const token = this.getToken();
      if (!token) {
        return SessionState.INVALID;
      }

      await ApiClient.post('/api/auth/verify', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return SessionState.VALID;
    } catch (error: any) {
      if (error.response?.status === 401) {
        return SessionState.EXPIRED;
      } else {
        return SessionState.NETWORK_ERROR;
      }
    }
  }

  /**
   * Obtiene la organización actual formateada
   */
  static getCurrentOrganization(): { id: number; name: string; rut?: string } | null {
    const empresa = this.getEmpresaActiva();
    return empresa ? {
      id: empresa.id,
      name: empresa.nombre || empresa.name,
      rut: empresa.rut
    } : null;
  }

  /**
   * Métodos de verificación de permisos avanzados
   */
  static hasAnyPermission(permissionKeys: string[]): boolean {
    return permissionKeys.some(permission => {
      const backendPermissions = this.getUAUPermissions();
      return backendPermissions[permission] || false;
    });
  }

  static hasAllPermissions(permissionKeys: string[]): boolean {
    return permissionKeys.every(permission => {
      const backendPermissions = this.getUAUPermissions();
      return backendPermissions[permission] || false;
    });
  }

  /**
   * Limpia el storage sin mostrar notificaciones
   */
  static clearStorageSilently(): void {
    this.clearStorage();
  }

  /**
   * Maneja sesión expirada
   */
  static handleSessionExpired(): void {
    this.clearStorageSilently();

    // Guardar información de contexto antes de redirigir
    const currentPath = window.location.pathname;
    if (currentPath !== '/login') {
      sessionStorage.setItem('camping_redirect_after_login', currentPath);
    }

    // Redirección con mensaje
    const loginUrl = '/login?expired=true';
    setTimeout(() => {
      window.location.href = loginUrl;
    }, 100);
  }

  /**
   * Redirige al login
   */
  static redirectToLogin(): void {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
}

export default AuthServiceUAU;
